import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  Alert,
  SafeAreaView,
  ScrollView,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import { useTheme } from '../context/ThemeContext';
import { cardEdgeStyle } from '../theme/cardEdge';
import SignupScreen from './SignupScreen';
import ForgotPasswordScreen from './ForgotPasswordScreen';
import AuthSignInForm from '../components/auth/AuthSignInForm';
import AuthSplitLayout from '../components/auth/AuthSplitLayout';
import { isWeb } from '../utils/platformUtils';
import { useAuth } from '../context/AuthContext';
import {
  formatSocialAuthError,
  getAuthErrorMessage,
  isInvalidCredentialError,
} from '../utils/authErrors';
import { RouteProp, useRoute } from '@react-navigation/native';

type AuthStackParamList = {
  Login: {
    initialScreen?: 'login' | 'signup';
  };
};

type LoginScreenRouteProp = RouteProp<AuthStackParamList, 'Login'>;

const validateEmail = (email: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);

const LoginScreen = () => {
  const route = useRoute<LoginScreenRouteProp>();
  const { colors } = useTheme();
  const { login, signInWithGoogle, signInWithApple } = useAuth();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [socialLoading, setSocialLoading] = useState<'google' | 'apple' | null>(null);
  const [socialError, setSocialError] = useState('');
  const [currentScreen, setCurrentScreen] = useState<'login' | 'signup' | 'forgot'>('login');
  const [validationErrors, setValidationErrors] = useState({
    email: '',
    password: '',
  });

  useEffect(() => {
    if (route.params?.initialScreen) {
      setCurrentScreen(route.params.initialScreen);
    }
  }, [route.params?.initialScreen]);

  const validateForm = () => {
    const errors = { email: '', password: '' };
    if (!email.trim()) {
      errors.email = 'Email is required';
    } else if (!validateEmail(email)) {
      errors.email = 'Please enter a valid email address';
    }
    if (!password.trim()) {
      errors.password = 'Password is required';
    }
    setValidationErrors(errors);
    return !errors.email && !errors.password;
  };

  const handleLogin = async () => {
    if (!validateForm()) return;

    setIsLoading(true);
    setValidationErrors({ email: '', password: '' });

    try {
      await login(email, password);
    } catch (error) {
      const errorMessage = getAuthErrorMessage(error);
      if (isInvalidCredentialError(error)) {
        setValidationErrors({ email: '', password: errorMessage });
      } else if (errorMessage.includes('Too many failed attempts')) {
        setValidationErrors({ email: '', password: errorMessage });
      } else if (errorMessage.includes('Network error')) {
        Alert.alert('Error', errorMessage);
      } else {
        setValidationErrors({ email: '', password: errorMessage });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const handleGoogleSignIn = async () => {
    setSocialError('');
    setValidationErrors({ email: '', password: '' });
    setSocialLoading('google');
    try {
      await signInWithGoogle();
    } catch (error) {
      const message = formatSocialAuthError(error, 'Google sign-in failed.');
      if (message) setSocialError(message);
    } finally {
      setSocialLoading(null);
    }
  };

  const handleAppleSignIn = async () => {
    setSocialError('');
    setValidationErrors({ email: '', password: '' });
    setSocialLoading('apple');
    try {
      await signInWithApple();
    } catch (error) {
      const message = formatSocialAuthError(error, 'Apple sign-in failed.');
      if (message) setSocialError(message);
    } finally {
      setSocialLoading(null);
    }
  };

  if (currentScreen === 'signup') {
    return <SignupScreen onBack={() => setCurrentScreen('login')} />;
  }

  if (currentScreen === 'forgot') {
    return (
      <ForgotPasswordScreen onBack={() => setCurrentScreen('login')} />
    );
  }

  const signInForm = (
    <AuthSignInForm
      email={email}
      onEmailChange={setEmail}
      password={password}
      onPasswordChange={setPassword}
      showPassword={showPassword}
      onTogglePassword={() => setShowPassword(!showPassword)}
      validationErrors={validationErrors}
      isLoading={isLoading}
      socialLoading={socialLoading}
      socialError={socialError}
      onSignIn={handleLogin}
      onGoogleSignIn={handleGoogleSignIn}
      onAppleSignIn={handleAppleSignIn}
      onForgotPassword={() => setCurrentScreen('forgot')}
      onCreateAccount={() => setCurrentScreen('signup')}
    />
  );

  if (isWeb) {
    return (
      <AuthSplitLayout title="Welcome back" subtitle="Sign in to your account">
        {signInForm}
      </AuthSplitLayout>
    );
  }

  return (
    <SafeAreaView style={[styles.container, { backgroundColor: colors.background.paper }]}>
      <KeyboardAvoidingView
        style={styles.flex}
        behavior={Platform.OS === 'ios' ? 'padding' : undefined}
      >
        <ScrollView
          contentContainerStyle={styles.mobileScroll}
          keyboardShouldPersistTaps="handled"
        >
          <View
            style={[
              styles.mobileCard,
              { backgroundColor: colors.surface },
              cardEdgeStyle(colors),
              colors.cardEdge === 'transparent' && { borderColor: colors.border },
            ]}
          >
            <Text style={[styles.mobileTitle, { color: colors.text.primary }]}>Welcome back</Text>
            <Text style={[styles.mobileSubtitle, { color: colors.text.secondary }]}>
              Sign in to your account
            </Text>
            {signInForm}
          </View>
        </ScrollView>
      </KeyboardAvoidingView>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
  },
  flex: {
    flex: 1,
  },
  mobileScroll: {
    flexGrow: 1,
    justifyContent: 'center',
    padding: 24,
  },
  mobileCard: {
    width: '100%',
    borderRadius: 16,
    padding: 24,
    borderWidth: 1,
  },
  mobileTitle: {
    fontSize: 26,
    fontWeight: '700',
    marginBottom: 6,
  },
  mobileSubtitle: {
    fontSize: 15,
    marginBottom: 24,
    lineHeight: 22,
  },
});

export default LoginScreen;
